import { useSyncExternalStore } from 'react';

/** What the My settings motion switch stores. `system` follows the operating system setting. */
export type MotionPreference = 'system' | 'reduced';

const QUERY = '(prefers-reduced-motion: reduce)';
const ATTRIBUTE = 'data-motion';

/**
 * Applies the person's motion preference to the document. The My settings switch calls this;
 * the global CSS and every animated component read `html[data-motion="reduced"]`.
 */
export function setMotionPreference(preference: MotionPreference): void {
  if (preference === 'reduced') {
    document.documentElement.setAttribute(ATTRIBUTE, 'reduced');
  } else {
    document.documentElement.removeAttribute(ATTRIBUTE);
  }
}

function mediaQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(QUERY) : null;
}

/** True when motion should be still right now (for loaders and timers outside React). */
export function prefersReducedMotion(): boolean {
  return readReducedMotion();
}

function readReducedMotion(): boolean {
  return (
    document.documentElement.getAttribute(ATTRIBUTE) === 'reduced' || mediaQuery()?.matches === true
  );
}

function subscribe(onChange: () => void): () => void {
  const query = mediaQuery();
  query?.addEventListener('change', onChange);
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: [ATTRIBUTE] });
  return () => {
    query?.removeEventListener('change', onChange);
    observer.disconnect();
  };
}

/** True when motion should be still: the OS asks for reduced motion, or the person turned it off. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, readReducedMotion, () => false);
}
